import { CRYPTO_ASSETS, NETWORKS } from '../../../lib/constants'
import { formatMYR, formatCrypto, validateWalletAddress } from '../../../lib/utils'
import { layout, ui, cx, text, ratePill, validationStyles } from '../../../lib/ui-primitives'
import { BackIcon } from '../../icons'
import type { useKiosk } from '../../../hooks/useKiosk'

interface DetailsScreenProps {
  kiosk: ReturnType<typeof useKiosk>
}

export function DetailsScreen({ kiosk }: DetailsScreenProps) {
  const {
    selectedCrypto,
    selectedNetwork,
    amount,
    cryptoAmount,
    rateLockRemaining,
    userDetails,
    setUserDetails,
    error,
    submitDetails,
    setScreen,
  } = kiosk

  if (!selectedCrypto || !selectedNetwork) return null

  const asset = CRYPTO_ASSETS[selectedCrypto]
  const network = NETWORKS[selectedNetwork]
  const addressValidation = validateWalletAddress(userDetails.walletAddress, selectedNetwork)

  return (
    <div className={layout.screenContent}>
      {/* Header with back and timer */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={() => setScreen('amount')}
          className={ui.backBtn}
          aria-label="Go back to amount entry"
        >
          <BackIcon aria-hidden="true" /> <span>Back</span>
        </button>
        {rateLockRemaining > 0 && (
          <div className={cx(ratePill.base, ratePill.active)}>
            <span aria-hidden="true">⏱</span>
            <span className={text.numeric}>
              {Math.floor(rateLockRemaining / 60)}:
              {(rateLockRemaining % 60).toString().padStart(2, '0')}
            </span>
          </div>
        )}
      </div>

      <div className="text-center mb-4 sm:mb-6">
        <h2 className={text.h2}>Your Details</h2>
        <p className={cx('text-sm mt-1', text.secondary)}>
          We'll contact you for verification
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="w-full max-w-sm mx-auto space-y-4">
          {/* Full Name */}
          <div>
            <label
              htmlFor="customer-name"
              className={cx('text-sm mb-2 block', text.secondary)}
            >
              Full Name *
            </label>
            <input
              id="customer-name"
              type="text"
              value={userDetails.name}
              onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
              placeholder="Ahmad bin Abdullah"
              className={ui.input}
              autoComplete="name"
            />
          </div>

          {/* Contact Method */}
          <div>
            <label className={cx('text-sm mb-2 block', text.secondary)}>
              Contact Method *
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                type="button"
                onClick={() =>
                  setUserDetails({ ...userDetails, contactType: 'telegram', contact: '' })
                }
                className={cx(
                  ui.btnBase,
                  'py-3',
                  userDetails.contactType === 'telegram'
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    : cx(ui.btnGhost, text.secondary)
                )}
              >
                <span aria-hidden="true">📱</span> Telegram
              </button>
              <button
                type="button"
                onClick={() =>
                  setUserDetails({ ...userDetails, contactType: 'email', contact: '' })
                }
                className={cx(
                  ui.btnBase,
                  'py-3',
                  userDetails.contactType === 'email'
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    : cx(ui.btnGhost, text.secondary)
                )}
              >
                <span aria-hidden="true">✉️</span> Email
              </button>
            </div>
            <input
              type={userDetails.contactType === 'email' ? 'email' : 'text'}
              inputMode={userDetails.contactType === 'email' ? 'email' : 'text'}
              value={userDetails.contact}
              onChange={(e) => setUserDetails({ ...userDetails, contact: e.target.value })}
              placeholder={
                userDetails.contactType === 'telegram' ? '@your_username' : 'you@email.com'
              }
              className={ui.input}
              autoComplete={userDetails.contactType === 'email' ? 'email' : 'username'}
              aria-label={userDetails.contactType === 'telegram' ? 'Telegram username' : 'Email address'}
            />
          </div>

          {/* Wallet Address */}
          <div>
            <label
              htmlFor="wallet-address"
              className={cx('text-sm mb-2 block', text.secondary)}
            >
              {selectedCrypto} Wallet Address ({selectedNetwork}) *
            </label>
            <textarea
              id="wallet-address"
              value={userDetails.walletAddress}
              onChange={(e) =>
                setUserDetails({ ...userDetails, walletAddress: e.target.value.trim() })
              }
              placeholder={`${network.addressPrefix}...`}
              rows={2}
              className={cx(ui.textarea, 'font-mono text-sm')}
              aria-describedby="wallet-validation"
            />
            <div id="wallet-validation" className="flex items-center gap-2 mt-1">
              <span
                className={cx(
                  'size-2 rounded-full',
                  addressValidation.valid
                    ? validationStyles.valid
                    : userDetails.walletAddress
                      ? validationStyles.invalid
                      : validationStyles.empty
                )}
                aria-hidden="true"
              />
              <span className={cx('text-xs', text.tertiary)}>
                {addressValidation.valid
                  ? `Valid ${network.name} address`
                  : userDetails.walletAddress
                    ? addressValidation.error || 'Invalid address format'
                    : `${network.name} address`}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className={ui.error} role="alert" aria-live="polite">
              {error}
            </div>
          )}

          {/* Summary Card */}
          <div className={cx(ui.card, 'p-4')}>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className={text.secondary}>Amount</span>
                <span className={cx('text-white', text.numeric)}>{formatMYR(amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className={text.secondary}>You receive</span>
                <span
                  className={cx('font-mono', text.numeric)}
                  style={{ color: asset.color }}
                >
                  {formatCrypto(cryptoAmount, 4)} {selectedCrypto}
                </span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={submitDetails}
            className={cx(ui.btnBase, ui.btnPrimary, 'w-full py-4 font-bold text-lg')}
            style={{ backgroundColor: asset.color, color: 'white' }}
          >
            Proceed to Payment
          </button>
        </div>
      </div>
    </div>
  )
}
